import { FC, useEffect, useState, useCallback } from "react"
import { useParams } from "react-router-dom"
import { observer } from "mobx-react"
import { Table, Button, Select, DatePicker } from "antd"
import { ReloadOutlined } from "@ant-design/icons"
import moment from "moment"
import { useStore } from "stores/utils/use-store"
import { lootboxColumns } from "./columns"
import { GrantLootbox } from "./grant-lootbox"
import { Lootbox, LootboxType, LootboxSource, LootboxStatus } from "../store/types"
import { getLootboxHistory } from "../api/accounts.call"

export const AccountLootboxes: FC = observer<FC>(() => {
  const { id } = useParams<{ id: string }>()
  const { dataStore: { accountDetailsStore } } = useStore()
  const [visibleGrantLootboxModal, setGrantLootboxModalVisibility] = useState<boolean>(false)
  const [selectedLootboxIds, setSelectedLootboxIds] = useState<number[]>([])
  const [lootboxes, setLootboxes] = useState<Lootbox[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [typeFilter, setTypeFilter] = useState<LootboxType | undefined>(undefined)
  const [sourceFilter, setSourceFilter] = useState<LootboxSource | undefined>(undefined)
  const [statusFilter, setStatusFilter] = useState<LootboxStatus | undefined>(undefined)
  const [obtainedAfter, setObtainedAfter] = useState<Date | undefined>(undefined)
  const [obtainedBefore, setObtainedBefore] = useState<Date | undefined>(undefined)

  const loadLootboxes = useCallback(async() => {
    if (!id) {
      return
    }
    setIsLoading(true)
    const result = await getLootboxHistory(id, {
      type: typeFilter,
      source: sourceFilter,
      status: statusFilter,
      obtainedAfter: obtainedAfter ? obtainedAfter.toISOString() : undefined,
      obtainedBefore: obtainedBefore ? obtainedBefore.toISOString() : undefined,
    })
    setLootboxes(result?.data || [])
    setIsLoading(false)
  }, [id, typeFilter, sourceFilter, statusFilter, obtainedAfter, obtainedBefore])

  useEffect(() => {
    loadLootboxes()
  }, [loadLootboxes])

  return (
    <>
      <div style={{ display: "flex", gap: "10px", marginBottom: "16px", flexWrap: "wrap" }}>
        <Select
          placeholder="Filter by Type"
          allowClear
          style={{ width: "200px" }}
          value={typeFilter}
          onChange={value => setTypeFilter(value)}
        >
          <Select.Option value={LootboxType.REGULAR}>Regular</Select.Option>
          <Select.Option value={LootboxType.PREMIUM}>Premium</Select.Option>
        </Select>
        <Select
          placeholder="Filter by Source"
          allowClear
          style={{ width: "200px" }}
          value={sourceFilter}
          onChange={value => setSourceFilter(value)}
        >
          <Select.Option value={LootboxSource.BATTLE_PASS}>Battle Pass</Select.Option>
          <Select.Option value={LootboxSource.ADMIN_GRANTED}>Admin Granted</Select.Option>
          <Select.Option value={LootboxSource.PROMOTION}>Promotion</Select.Option>
        </Select>
        <Select
          placeholder="Filter by Status"
          allowClear
          style={{ width: "200px" }}
          value={statusFilter}
          onChange={value => setStatusFilter(value)}
        >
          <Select.Option value={LootboxStatus.UNOPENED}>Unopened</Select.Option>
          <Select.Option value={LootboxStatus.OPENED}>Opened</Select.Option>
          <Select.Option value={LootboxStatus.REFUNDED}>Refunded</Select.Option>
        </Select>
        <DatePicker
          showTime
          format="YYYY-MM-DD HH:mm"
          placeholder="Obtained After"
          style={{ width: "200px" }}
          value={obtainedAfter ? moment(obtainedAfter) : undefined}
          onChange={e => setObtainedAfter(e?.toDate())}
        />
        <DatePicker
          showTime
          format="YYYY-MM-DD HH:mm"
          placeholder="Obtained Before"
          style={{ width: "200px" }}
          value={obtainedBefore ? moment(obtainedBefore) : undefined}
          onChange={e => setObtainedBefore(e?.toDate())}
        />
      </div>
      <div style={{ marginBottom: "16px" }}>
        <Button
          type="primary"
          onClick={() => setGrantLootboxModalVisibility(true)}
          style={{ marginRight: "8px" }}
        >
          Grant Lootbox
        </Button>
        <Button
          danger
          onClick={async() => {
            if (selectedLootboxIds.length > 0) {
              accountDetailsStore.funcAfterSuccess = async() => {
                setSelectedLootboxIds([])
                accountDetailsStore.resetFuncAfterSuccess()
                await loadLootboxes()
              }
              await accountDetailsStore.revokeLootboxes(selectedLootboxIds)
            }
          }}
          disabled={selectedLootboxIds.length === 0}
          style={{ marginRight: "8px" }}
        >
          Revoke Lootboxes
        </Button>
        <Button
          icon={<ReloadOutlined />}
          onClick={loadLootboxes}
          loading={isLoading}
        >
          Reload
        </Button>
      </div>
      <Table
        dataSource={lootboxes}
        columns={lootboxColumns as any}
        rowKey="id"
        rowSelection={{
          selectedRowKeys: selectedLootboxIds,
          onChange: (selectedRowKeys: React.Key[]) => {
            setSelectedLootboxIds(
              selectedRowKeys
                .map(key => typeof key === "number" ? key : (typeof key === "string" && !isNaN(Number(key)) ? Number(key) : null))
                .filter((key): key is number => typeof key === "number" && key !== null),
            )
          },
        }}
        loading={isLoading}
      />
      <GrantLootbox
        visible={visibleGrantLootboxModal}
        onClose={() => setGrantLootboxModalVisibility(false)}
        onSuccess={loadLootboxes}
      />
    </>
  )
})

